import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { eventService } from '../services/eventService';
import { geoService } from '../services/geoService';
import { uomService } from '../services/uomService';
import {
    toCanonicalVisibility,
    toDiscoverPrivacy,
} from '../utils/discoverVisibility';
import {
    DEFAULT_BOUNDS_PRECISION,
    isCoordinateWithinBounds,
    sanitizeBounds,
    sanitizeMarkers,
    serializeBoundsKey,
} from '../utils/mapConfig';
import { isPublicDiscoverMarker } from '../utils/discoverMarkerVisibility';
import { subscribeVenueAdminVerified } from '../utils/venueAdminEvents';

const DEFAULT_TTL_MS = 300_000;
const DEFAULT_CACHE_MAX_ENTRIES = 64;
const DEFAULT_SCHEDULED_DURATION_MS = 2 * 60 * 60 * 1000;
const EVENT_WRITE_TYPES = new Set(['EVENT', 'SESSION', 'VENUE']);
const SCHEDULED_STATUSES = new Set(['DRAFT', 'PUBLISHED', 'CANCELLED', 'COMPLETED']);
const JOIN_POLICIES = new Set(['OPEN', 'APPROVAL_REQUIRED', 'CLOSED']);
const CANONICAL_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const ORGANIZER_OBJECT_TYPE_USER = 'USER';
const ORGANIZER_OBJECT_TYPE_BAND = 'BAND';
const DEFAULT_JOIN_POLICY = 'OPEN';
const DEFAULT_SCHEDULED_STATUS = 'PUBLISHED';
const DISCOVER_MARKER_SANITIZE_OPTIONS = Object.freeze({ privacyFallback: 'PRIVATE' });

function sanitizePositiveInteger(value, defaultValue) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.floor(value);
    }
    if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number.parseInt(value, 10);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return defaultValue;
}

function sanitizeTypeFilter(types) {
    if (!Array.isArray(types)) {
        return null;
    }
    const normalized = new Set();
    types.forEach((type) => {
        if (typeof type !== 'string') {
            return;
        }
        const value = type.trim().toUpperCase();
        if (value) {
            normalized.add(value);
        }
    });
    return Array.from(normalized).sort();
}

function sanitizeFetchCenter(center) {
    if (!center || typeof center !== 'object') {
        return null;
    }
    const latitude = center.lat ?? center.latitude;
    const longitude = center.lng ?? center.lon ?? center.longitude;
    if (typeof latitude !== 'number' || !Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
        return null;
    }
    if (typeof longitude !== 'number' || !Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
        return null;
    }
    return Object.freeze({ lat: latitude, lng: longitude });
}

function serializeCenterKey(center, decimals = DEFAULT_BOUNDS_PRECISION) {
    if (!center) {
        return 'center:none';
    }
    const factor = 10 ** decimals;
    const lat = Math.round(center.lat * factor) / factor;
    const lng = Math.round(center.lng * factor) / factor;
    return `center:${lat.toFixed(decimals)}|${lng.toFixed(decimals)}`;
}

function isCacheFresh(cache, ttlMs) {
    if (!Array.isArray(cache.data) || !cache.fetchedAt) {
        return false;
    }
    return (Date.now() - cache.fetchedAt) < ttlMs;
}

function hasCachedPayload(cache) {
    return Array.isArray(cache?.data);
}

function hasCachedMarkers(cache) {
    return hasCachedPayload(cache) && cache.data.length > 0;
}

const envTtlMs = sanitizePositiveInteger(
    process.env.REACT_APP_MAP_EVENTS_TTL_MS,
    DEFAULT_TTL_MS,
);
const envCacheMaxEntries = sanitizePositiveInteger(
    process.env.REACT_APP_MAP_EVENTS_CACHE_MAX_ENTRIES,
    DEFAULT_CACHE_MAX_ENTRIES,
);

const markersCache = new Map();
const UNSET_INVALIDATE_KEY = Symbol('unset-discover-invalidate-key');
const userObjectIdCache = {
    promise: null,
};

function createCacheEntry({ key, bounds, types, now = Date.now() }) {
    return {
        key,
        bounds,
        types,
        data: null,
        fetchedAt: 0,
        lastAccessedAt: now,
        promise: null,
        error: null,
    };
}

function resolveCacheEntryAge(entry) {
    if (typeof entry?.lastAccessedAt === 'number' && Number.isFinite(entry.lastAccessedAt)) {
        return entry.lastAccessedAt;
    }
    if (typeof entry?.fetchedAt === 'number' && Number.isFinite(entry.fetchedAt)) {
        return entry.fetchedAt;
    }
    return 0;
}

function isPrunableExpiredCacheEntry(entry, ttlMs, now) {
    if (!entry || entry.promise) {
        return false;
    }
    const ageReference = entry.fetchedAt || entry.lastAccessedAt;
    return typeof ageReference === 'number'
        && Number.isFinite(ageReference)
        && ageReference > 0
        && (now - ageReference) >= ttlMs;
}

function pruneMarkersCache({ ttlMs = envTtlMs, maxEntries = envCacheMaxEntries, protectedKey = null, now = Date.now() } = {}) {
    const effectiveTtlMs = sanitizePositiveInteger(ttlMs, envTtlMs);
    const effectiveMaxEntries = sanitizePositiveInteger(maxEntries, envCacheMaxEntries);

    markersCache.forEach((entry, key) => {
        if (key === protectedKey || entry?.promise) {
            return;
        }
        if (isPrunableExpiredCacheEntry(entry, effectiveTtlMs, now)) {
            markersCache.delete(key);
        }
    });

    if (markersCache.size <= effectiveMaxEntries) {
        return;
    }

    const candidates = Array.from(markersCache.entries())
        .filter(([key, entry]) => key !== protectedKey && !entry?.promise)
        .sort(([, first], [, second]) => resolveCacheEntryAge(first) - resolveCacheEntryAge(second));

    for (const [key] of candidates) {
        if (markersCache.size <= effectiveMaxEntries) {
            break;
        }
        markersCache.delete(key);
    }
}

function getCacheEntry(key, bounds, types = null, options = {}) {
    const now = Date.now();
    if (!markersCache.has(key)) {
        markersCache.set(key, createCacheEntry({ key, bounds, types, now }));
    }
    const entry = markersCache.get(key);
    entry.lastAccessedAt = now;
    if (bounds) {
        entry.bounds = bounds;
    }
    entry.types = Array.isArray(types) ? [...types] : null;
    pruneMarkersCache({ ...options, protectedKey: key, now });
    return entry;
}

function cacheEntryAllowsMarkerType(entry, markerType) {
    if (!Array.isArray(entry?.types) || entry.types.length === 0) {
        return true;
    }
    return entry.types.includes(markerType);
}

function findCachedMarkerType(markerId) {
    const normalizedMarkerId = readOptionalString(markerId);
    if (!normalizedMarkerId) {
        return '';
    }

    for (const entry of markersCache.values()) {
        if (!Array.isArray(entry?.data)) {
            continue;
        }
        const existingMarker = entry.data.find((marker) => marker?.id === normalizedMarkerId);
        if (existingMarker) {
            return normalizeMarkerType(existingMarker.type);
        }
    }
    return '';
}

function invalidateMarkersCache() {
    markersCache.clear();
}

export function invalidateDiscoverMarkersCache() {
    invalidateMarkersCache();
}

export function __resetDiscoverMarkersTestState() {
    invalidateMarkersCache();
    userObjectIdCache.promise = null;
}

export function __getDiscoverMarkersCacheSize() {
    return markersCache.size;
}

function normalizeMarkerType(type) {
    if (typeof type !== 'string') {
        return '';
    }
    return type.trim().toUpperCase();
}

function readTrimmedString(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value).trim();
    }
    return '';
}

function readOptionalString(value, maxLength) {
    const trimmed = readTrimmedString(value);
    if (!trimmed) {
        return undefined;
    }
    if (typeof maxLength === 'number' && maxLength > 0) {
        return trimmed.slice(0, maxLength);
    }
    return trimmed;
}

function requireNonEmptyString(value, fieldName, maxLength) {
    const normalized = readOptionalString(value, maxLength);
    if (!normalized) {
        throw new Error(`${fieldName} is required`);
    }
    return normalized;
}

function isCanonicalUuid(value) {
    if (typeof value !== 'string') {
        return false;
    }
    return CANONICAL_UUID_REGEX.test(value.trim());
}

function requireCanonicalUuid(value, fieldName) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (!isCanonicalUuid(trimmed)) {
        throw new Error(`${fieldName} must be a canonical UUID`);
    }
    return trimmed;
}

function normalizeMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return {};
    }
    return { ...metadata };
}

function toVisibility(privacy) {
    return toCanonicalVisibility(privacy);
}

function toPrivacy(visibility) {
    return toDiscoverPrivacy(visibility);
}

function shouldKeepDiscoverMarker(marker) {
    if (!isPublicDiscoverMarker(marker)) {
        return false;
    }

    const markerType = normalizeMarkerType(marker?.type);
    if (!EVENT_WRITE_TYPES.has(markerType)) {
        return true;
    }

    if (markerType === 'VENUE') {
        return true;
    }

    const status = normalizeScheduledStatus(marker?.metadata?.status);
    return status === 'PUBLISHED';
}

function requireCoordinates(payload) {
    const latitude = payload?.coordinate?.latitude;
    const longitude = payload?.coordinate?.longitude;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        throw new Error('Valid marker coordinates are required');
    }
    return {
        latitude,
        longitude,
    };
}

function parseIsoDate(value, fieldName) {
    if (value == null) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error(`${fieldName} must be a valid date-time value`);
    }
    return date;
}

function resolveTimezone(metadata) {
    const fromMetadata = readOptionalString(metadata.timezone, 64);
    if (fromMetadata) {
        return fromMetadata;
    }
    try {
        const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (typeof resolved === 'string' && resolved.trim()) {
            return resolved.trim();
        }
    } catch (_) {
        // no-op
    }
    return 'UTC';
}

function resolveScheduledDates(metadata) {
    const startRaw = metadata.startDate ?? metadata.startAt;
    const startDate = parseIsoDate(startRaw, 'startDate');
    if (!startDate) {
        throw new Error('startDate is required for events and sessions');
    }

    const endRaw = metadata.endDate ?? metadata.endAt;
    const endDate = endRaw == null
        ? new Date(startDate.getTime() + DEFAULT_SCHEDULED_DURATION_MS)
        : parseIsoDate(endRaw, 'endDate');

    if (!(endDate.getTime() > startDate.getTime())) {
        throw new Error('endDate must be after startDate');
    }

    return {
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
    };
}

function normalizeJoinPolicy(value) {
    const normalized = normalizeMarkerType(value);
    return JOIN_POLICIES.has(normalized) ? normalized : DEFAULT_JOIN_POLICY;
}

function normalizeScheduledStatus(value) {
    const normalized = normalizeMarkerType(value);
    return SCHEDULED_STATUSES.has(normalized) ? normalized : DEFAULT_SCHEDULED_STATUS;
}

function normalizeCountryCode(value) {
    const normalized = readOptionalString(value, 2);
    if (!normalized) {
        return undefined;
    }
    return normalized.toUpperCase();
}

function normalizeCapacity(value) {
    if (value == null || value === '') {
        return undefined;
    }
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
        throw new Error('capacity must be a non-negative integer');
    }
    return parsed;
}

function normalizeAttendeeLimit(value) {
    if (value == null || value === '') {
        return undefined;
    }
    const parsed = Number.parseInt(String(value), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        throw new Error('attendeeLimit must be a positive integer');
    }
    return parsed;
}

function normalizeAmenities(value) {
    if (value == null) {
        return undefined;
    }
    if (!Array.isArray(value)) {
        throw new Error('amenities must be an array');
    }
    if (value.length > 100) {
        throw new Error('amenities must contain at most 100 items');
    }
    return value.map((item) => requireNonEmptyString(item, 'amenities', 64));
}

function readMetadataField(metadata, key, existingValue) {
    if (Object.prototype.hasOwnProperty.call(metadata, key) && metadata[key] !== undefined) {
        return metadata[key];
    }
    return existingValue;
}

async function resolveCurrentUserObjectId() {
    if (userObjectIdCache.promise) {
        return userObjectIdCache.promise;
    }
    userObjectIdCache.promise = uomService.getCurrentUserObject()
        .then((response) => {
            return requireCanonicalUuid(response?.objectId, 'organizerObjectId');
        })
        .finally(() => {
            userObjectIdCache.promise = null;
        });
    return userObjectIdCache.promise;
}

function resolveOrganizerFromExistingEntity(existingEntity = {}) {
    const organizerType = normalizeMarkerType(
        existingEntity?.organizer?.objectType ?? existingEntity?.organizerObjectType
    );
    const organizerObjectId = readOptionalString(
        existingEntity?.organizer?.objectId ?? existingEntity?.organizerObjectId
    );

    if (!organizerType || !organizerObjectId) {
        return null;
    }

    if (organizerType !== ORGANIZER_OBJECT_TYPE_USER && organizerType !== ORGANIZER_OBJECT_TYPE_BAND) {
        return null;
    }

    return {
        organizerObjectType: organizerType,
        organizerObjectId: requireCanonicalUuid(organizerObjectId, 'organizerObjectId'),
    };
}

async function resolveOrganizer(metadata, existingEntity = {}) {
    const hasOrganizerType = Object.prototype.hasOwnProperty.call(metadata, 'organizerObjectType')
        && metadata.organizerObjectType !== undefined;
    const hasOrganizerObjectId = Object.prototype.hasOwnProperty.call(metadata, 'organizerObjectId')
        && metadata.organizerObjectId !== undefined;

    if (!hasOrganizerType && !hasOrganizerObjectId) {
        const existingOrganizer = resolveOrganizerFromExistingEntity(existingEntity);
        if (existingOrganizer) {
            return existingOrganizer;
        }
    }

    const organizerType = normalizeMarkerType(metadata.organizerObjectType);
    const organizerObjectId = readOptionalString(metadata.organizerObjectId);

    if (organizerType === ORGANIZER_OBJECT_TYPE_BAND) {
        if (!organizerObjectId) {
            throw new Error('organizerObjectId is required for BAND organizers');
        }
        return {
            organizerObjectType: ORGANIZER_OBJECT_TYPE_BAND,
            organizerObjectId: requireCanonicalUuid(organizerObjectId, 'organizerObjectId'),
        };
    }

    if (organizerType === ORGANIZER_OBJECT_TYPE_USER && organizerObjectId) {
        return {
            organizerObjectType: ORGANIZER_OBJECT_TYPE_USER,
            organizerObjectId: requireCanonicalUuid(organizerObjectId, 'organizerObjectId'),
        };
    }

    const currentUserObjectId = await resolveCurrentUserObjectId();
    return {
        organizerObjectType: ORGANIZER_OBJECT_TYPE_USER,
        organizerObjectId: currentUserObjectId,
    };
}

function toVenueWriteRequest(payload, existingVenue = {}) {
    const metadata = normalizeMetadata(payload?.metadata);
    const coordinates = requireCoordinates(payload);
    const amenities = normalizeAmenities(readMetadataField(metadata, 'amenities', existingVenue?.amenities));

    const request = {
        name: requireNonEmptyString(payload?.name, 'name', 255),
        description: readOptionalString(metadata.description, 4000),
        addressLine: requireNonEmptyString(metadata.address, 'address', 255),
        city: requireNonEmptyString(metadata.city, 'city', 128),
        stateOrRegion: readOptionalString(metadata.stateOrRegion, 128),
        country: requireNonEmptyString(metadata.country, 'country', 128),
        countryCode: normalizeCountryCode(metadata.countryCode),
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        website: readOptionalString(metadata.website, 512),
        phone: readOptionalString(metadata.phone, 64),
        capacity: normalizeCapacity(metadata.capacity),
        visibility: toVisibility(payload?.privacy),
    };

    if (amenities !== undefined) {
        request.amenities = amenities;
    }

    return request;
}

async function toScheduledWriteRequest(payload, existingEntity = {}) {
    const metadata = normalizeMetadata(payload?.metadata);
    const coordinates = requireCoordinates(payload);
    const organizer = await resolveOrganizer(metadata, existingEntity);
    const { startAt, endAt } = resolveScheduledDates(metadata);
    const venueObjectId = readOptionalString(metadata.venueObjectId);
    const joinPolicy = normalizeJoinPolicy(readMetadataField(metadata, 'joinPolicy', existingEntity?.joinPolicy));
    const attendeeLimit = normalizeAttendeeLimit(readMetadataField(
        metadata,
        'attendeeLimit',
        existingEntity?.attendeeLimit,
    ));
    const status = normalizeScheduledStatus(readMetadataField(metadata, 'status', existingEntity?.status));

    const request = {
        title: requireNonEmptyString(payload?.name, 'name', 255),
        description: readOptionalString(metadata.description, 4000),
        startAt,
        endAt,
        timezone: resolveTimezone(metadata),
        venueObjectId: venueObjectId ? requireCanonicalUuid(venueObjectId, 'venueObjectId') : undefined,
        locationName: readOptionalString(metadata.locationName ?? metadata.location, 255),
        addressLine: readOptionalString(metadata.address, 255),
        city: requireNonEmptyString(metadata.city, 'city', 128),
        stateOrRegion: readOptionalString(metadata.stateOrRegion, 128),
        country: requireNonEmptyString(metadata.country, 'country', 128),
        countryCode: normalizeCountryCode(metadata.countryCode),
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        organizerObjectType: organizer.organizerObjectType,
        organizerObjectId: organizer.organizerObjectId,
        joinPolicy,
        attendeeLimit,
        visibility: toVisibility(payload?.privacy),
        status,
    };

    return request;
}

function toScheduledMarker(response, payload, expectedType) {
    const metadata = normalizeMetadata(payload?.metadata);
    const location = response?.location || {};

    return {
        id: response?.id,
        name: typeof response?.title === 'string' ? response.title : payload?.name,
        type: normalizeMarkerType(response?.objectType) || expectedType,
        coordinate: {
            latitude: location.latitude ?? payload?.coordinate?.latitude,
            longitude: location.longitude ?? payload?.coordinate?.longitude,
        },
        metadata: {
            ...metadata,
            city: location.city ?? metadata.city,
            description: response?.description ?? metadata.description,
            startDate: response?.startAt ?? metadata.startDate ?? metadata.startAt,
            endDate: response?.endAt ?? metadata.endDate ?? metadata.endAt,
            status: response?.status ?? metadata.status,
            timezone: response?.timezone ?? metadata.timezone,
            location: location.locationName ?? metadata.location,
            address: location.addressLine ?? metadata.address,
            stateOrRegion: location.stateOrRegion ?? metadata.stateOrRegion,
            country: location.country ?? metadata.country,
            countryCode: location.countryCode ?? metadata.countryCode,
            organizerObjectType: response?.organizer?.objectType ?? metadata.organizerObjectType,
            organizerObjectId: response?.organizer?.objectId ?? metadata.organizerObjectId,
            venueObjectId: response?.venue?.objectId ?? metadata.venueObjectId,
            joinPolicy: response?.joinPolicy ?? metadata.joinPolicy,
            attendeeLimit: response?.attendeeLimit ?? metadata.attendeeLimit,
        },
        privacy: toPrivacy(response?.visibility),
        createdBy: response?.ownerId,
        createdAt: response?.createdAt,
        updatedAt: response?.updatedAt,
    };
}

function toVenueMarker(response, payload) {
    const metadata = normalizeMetadata(payload?.metadata);
    const location = response?.location || {};

    return {
        id: response?.id,
        name: typeof response?.name === 'string' ? response.name : payload?.name,
        type: normalizeMarkerType(response?.objectType) || 'VENUE',
        coordinate: {
            latitude: location.latitude ?? payload?.coordinate?.latitude,
            longitude: location.longitude ?? payload?.coordinate?.longitude,
        },
        metadata: {
            ...metadata,
            city: location.city ?? metadata.city,
            description: response?.description ?? metadata.description,
            address: location.addressLine ?? metadata.address,
            stateOrRegion: location.stateOrRegion ?? metadata.stateOrRegion,
            country: location.country ?? metadata.country,
            countryCode: location.countryCode ?? metadata.countryCode,
            website: response?.website ?? metadata.website,
            phone: response?.phone ?? metadata.phone,
            capacity: response?.capacity ?? metadata.capacity,
            amenities: response?.amenities ?? metadata.amenities,
        },
        privacy: toPrivacy(response?.visibility),
        createdBy: response?.ownerId,
        createdAt: response?.createdAt,
        updatedAt: response?.updatedAt,
    };
}

async function createDiscoverMarker(payload) {
    const markerType = normalizeMarkerType(payload?.type);
    if (!EVENT_WRITE_TYPES.has(markerType)) {
        return geoService.createPoint(payload);
    }

    if (markerType === 'VENUE') {
        const request = toVenueWriteRequest(payload);
        const created = await eventService.createVenue(request);
        return toVenueMarker(created, payload);
    }

    const request = await toScheduledWriteRequest(payload);
    if (markerType === 'EVENT') {
        const created = await eventService.createEvent(request);
        return toScheduledMarker(created, payload, 'EVENT');
    }
    const created = await eventService.createSession(request);
    return toScheduledMarker(created, payload, 'SESSION');
}

async function updateDiscoverMarker(markerId, payload, existingMarkerType = '') {
    const markerType = normalizeMarkerType(existingMarkerType) || normalizeMarkerType(payload?.type);
    if (!EVENT_WRITE_TYPES.has(markerType)) {
        return geoService.updatePoint(markerId, payload);
    }

    const normalizedObjectId = requireCanonicalUuid(markerId, 'marker id');

    if (markerType === 'VENUE') {
        const currentVenue = await eventService.getVenue(normalizedObjectId);
        const request = toVenueWriteRequest(payload, currentVenue);
        const updated = await eventService.updateVenue(normalizedObjectId, request);
        return toVenueMarker(updated, payload);
    }

    const currentScheduled = markerType === 'EVENT'
        ? await eventService.getEvent(normalizedObjectId)
        : await eventService.getSession(normalizedObjectId);
    const request = await toScheduledWriteRequest(payload, currentScheduled);
    if (markerType === 'EVENT') {
        const updated = await eventService.updateEvent(normalizedObjectId, request);
        return toScheduledMarker(updated, payload, 'EVENT');
    }
    const updated = await eventService.updateSession(normalizedObjectId, request);
    return toScheduledMarker(updated, payload, 'SESSION');
}

export function useDiscoverMarkers(options = {}) {
    const ttlMs = sanitizePositiveInteger(options.ttlMs, envTtlMs);
    const cacheMaxEntries = sanitizePositiveInteger(options.cacheMaxEntries, envCacheMaxEntries);
    const markerLimit = sanitizePositiveInteger(options.limit, null);
    const typeFilterSignature = Array.isArray(options.types)
        ? sanitizeTypeFilter(options.types).join(',')
        : null;
    const typeFilter = useMemo(
        () => {
            if (typeFilterSignature === null) {
                return null;
            }
            return typeFilterSignature ? typeFilterSignature.split(',') : [];
        },
        [typeFilterSignature],
    );
    const hasTypeFilter = Array.isArray(typeFilter);
    const isTypeFilterEmpty = hasTypeFilter && typeFilter.length === 0;
    const invalidateKey = options.invalidateKey;
    const requireBounds = options.requireBounds === true;
    const resolvedCenter = useMemo(
        () => sanitizeFetchCenter(options.center),
        [options.center],
    );
    const resolvedBounds = useMemo(
        () => sanitizeBounds(options.bounds),
        [options.bounds],
    );
    const boundsKey = useMemo(
        () => serializeBoundsKey(resolvedBounds, DEFAULT_BOUNDS_PRECISION),
        [resolvedBounds],
    );
    const centerKey = useMemo(
        () => serializeCenterKey(resolvedCenter, DEFAULT_BOUNDS_PRECISION),
        [resolvedCenter],
    );
    const typesKey = useMemo(
        () => (hasTypeFilter ? `types:${typeFilter.join(',')}` : 'types:all'),
        [hasTypeFilter, typeFilter],
    );
    const cacheKey = useMemo(
        () => [
            boundsKey,
            centerKey,
            markerLimit ? `limit:${markerLimit}` : 'limit:none',
            typesKey,
        ].join('|'),
        [boundsKey, centerKey, markerLimit, typesKey],
    );
    const resolveCacheEntry = useCallback(
        () => getCacheEntry(cacheKey, resolvedBounds, typeFilter, {
            ttlMs,
            maxEntries: cacheMaxEntries,
        }),
        [cacheKey, cacheMaxEntries, resolvedBounds, ttlMs, typeFilter],
    );
    const cacheEntry = useMemo(
        () => resolveCacheEntry(),
        [resolveCacheEntry],
    );
    const lastSuccessfulUpdatedRef = useRef(null);
    const lastInvalidateKeyRef = useRef(UNSET_INVALIDATE_KEY);
    const shouldInvalidateForKey = invalidateKey !== undefined
        && invalidateKey !== lastInvalidateKeyRef.current;

    const resolveStableLastUpdated = (value) => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            lastSuccessfulUpdatedRef.current = value;
            return value;
        }
        return lastSuccessfulUpdatedRef.current;
    };

    const [state, setState] = useState(() => {
        if ((requireBounds && !resolvedBounds) || isTypeFilterEmpty) {
            return {
                markers: [],
                isLoading: !isTypeFilterEmpty,
                error: null,
                lastUpdated: resolveStableLastUpdated(null),
                markerDataKey: null,
                isStale: false,
            };
        }

        if (shouldInvalidateForKey) {
            return {
                markers: [],
                isLoading: true,
                error: null,
                lastUpdated: resolveStableLastUpdated(null),
                markerDataKey: null,
                isStale: false,
            };
        }

        const cachedPayloadAvailable = hasCachedPayload(cacheEntry);
        const cacheFresh = cachedPayloadAvailable && isCacheFresh(cacheEntry, ttlMs);
        const cachedMarkersAvailable = hasCachedMarkers(cacheEntry);

        return {
            markers: cachedPayloadAvailable ? cacheEntry.data : [],
            isLoading: !cacheFresh,
            error: cacheEntry.error,
            lastUpdated: cachedPayloadAvailable
                ? resolveStableLastUpdated(cacheEntry.fetchedAt)
                : resolveStableLastUpdated(null),
            markerDataKey: cachedPayloadAvailable ? boundsKey : null,
            isStale: cachedMarkersAvailable && !cacheFresh,
        };
    });

    const fetchMarkers = useCallback(async ({ forceRefresh = false } = {}) => {
        if ((requireBounds && !resolvedBounds) || isTypeFilterEmpty) {
            return [];
        }
        const currentCache = resolveCacheEntry();
        const cacheFresh = isCacheFresh(currentCache, ttlMs);

        if (!forceRefresh && cacheFresh && currentCache.data) {
            return currentCache.data;
        }

        if (currentCache.promise) {
            return currentCache.promise;
        }

        const promise = geoService.fetchPoints({
            bounds: resolvedBounds,
            center: resolvedCenter,
            limit: markerLimit,
            types: typeFilter || undefined,
        })
            .then((payload) => {
                const markers = sanitizeMarkers(payload, DISCOVER_MARKER_SANITIZE_OPTIONS)
                    .filter(shouldKeepDiscoverMarker);
                currentCache.data = markers;
                currentCache.fetchedAt = Date.now();
                currentCache.error = null;
                return markers;
            })
            .catch((error) => {
                currentCache.error = error;
                throw error;
            })
            .finally(() => {
                currentCache.promise = null;
                pruneMarkersCache({
                    ttlMs,
                    maxEntries: cacheMaxEntries,
                    protectedKey: currentCache.key,
                });
            });

        currentCache.promise = promise;
        return promise;
    }, [cacheMaxEntries, isTypeFilterEmpty, markerLimit, requireBounds, resolveCacheEntry, resolvedBounds, resolvedCenter, ttlMs, typeFilter]);

    useEffect(() => {
        let isCancelled = false;

        const bootstrap = async () => {
            if ((requireBounds && !resolvedBounds) || isTypeFilterEmpty) {
                if (invalidateKey === undefined) {
                    lastInvalidateKeyRef.current = UNSET_INVALIDATE_KEY;
                } else {
                    lastInvalidateKeyRef.current = invalidateKey;
                }
                setState({
                    markers: [],
                    isLoading: !isTypeFilterEmpty,
                    error: null,
                    lastUpdated: resolveStableLastUpdated(null),
                    markerDataKey: null,
                    isStale: false,
                });
                return;
            }

            if (shouldInvalidateForKey) {
                invalidateMarkersCache();
            }
            if (invalidateKey === undefined) {
                lastInvalidateKeyRef.current = UNSET_INVALIDATE_KEY;
            } else {
                lastInvalidateKeyRef.current = invalidateKey;
            }

            const currentCache = resolveCacheEntry();
            const cachedPayloadAvailable = hasCachedPayload(currentCache);
            const cacheFresh = cachedPayloadAvailable && isCacheFresh(currentCache, ttlMs);
            const cachedMarkersAvailable = hasCachedMarkers(currentCache);

            if (cachedPayloadAvailable) {
                setState({
                    markers: currentCache.data,
                    isLoading: !cacheFresh,
                    error: currentCache.error,
                    lastUpdated: resolveStableLastUpdated(currentCache.fetchedAt),
                    markerDataKey: boundsKey,
                    isStale: cachedMarkersAvailable && !cacheFresh,
                });
            } else {
                setState((prev) => {
                    const hasPreviousMarkers = Array.isArray(prev.markers) && prev.markers.length > 0;

                    return {
                        ...prev,
                        isLoading: true,
                        error: null,
                        // The UI is still rendering the previous viewport while the
                        // next uncached viewport request is in flight.
                        isStale: prev.isStale || hasPreviousMarkers,
                    };
                });
            }

            if (cacheFresh && cachedPayloadAvailable) {
                return;
            }

            try {
                const markers = await fetchMarkers({ forceRefresh: !cacheFresh });
                if (isCancelled) {
                    return;
                }
                const refreshedCache = resolveCacheEntry();
                setState({
                    markers,
                    isLoading: false,
                    error: null,
                    lastUpdated: resolveStableLastUpdated(refreshedCache.fetchedAt),
                    markerDataKey: boundsKey,
                    isStale: false,
                });
            } catch (error) {
                if (isCancelled || error?.name === 'AbortError') {
                    return;
                }
                const refreshedCache = resolveCacheEntry();
                const hasScopedFallbackPayload = hasCachedPayload(refreshedCache);
                const hasScopedFallbackMarkers = Array.isArray(refreshedCache.data)
                    && refreshedCache.data.length > 0;
                setState((prev) => ({
                    markers: hasScopedFallbackPayload ? refreshedCache.data : [],
                    isLoading: false,
                    error,
                    lastUpdated: hasScopedFallbackPayload
                        ? resolveStableLastUpdated(refreshedCache.fetchedAt || null)
                        : resolveStableLastUpdated(prev.lastUpdated),
                    markerDataKey: hasScopedFallbackMarkers ? boundsKey : null,
                    isStale: hasScopedFallbackMarkers,
                }));
            }
        };

        bootstrap();

        return () => {
            isCancelled = true;
        };
    }, [boundsKey, fetchMarkers, invalidateKey, isTypeFilterEmpty, requireBounds, resolveCacheEntry, resolvedBounds, shouldInvalidateForKey, ttlMs]);

    const refresh = useCallback(async () => {
        if ((requireBounds && !resolvedBounds) || isTypeFilterEmpty) {
            setState({
                markers: [],
                isLoading: false,
                error: null,
                lastUpdated: resolveStableLastUpdated(null),
                markerDataKey: null,
                isStale: false,
            });
            return [];
        }
        setState((prev) => ({
            ...prev,
            isLoading: true,
            isStale: false,
        }));
        try {
            const markers = await fetchMarkers({ forceRefresh: true });
            const refreshedCache = resolveCacheEntry();
            setState({
                markers,
                isLoading: false,
                error: null,
                lastUpdated: resolveStableLastUpdated(refreshedCache.fetchedAt),
                markerDataKey: boundsKey,
                isStale: false,
            });
            return markers;
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isLoading: false,
                error,
                isStale: true,
            }));
            throw error;
        }
    }, [boundsKey, fetchMarkers, isTypeFilterEmpty, requireBounds, resolveCacheEntry, resolvedBounds]);

    useEffect(() => subscribeVenueAdminVerified(() => {
        invalidateMarkersCache();
        if ((requireBounds && !resolvedBounds) || isTypeFilterEmpty) {
            return;
        }
        refresh().catch(() => null);
    }), [refresh, isTypeFilterEmpty, requireBounds, resolvedBounds]);

    const updateCachesWithMarker = useCallback((marker) => {
        if (!marker) {
            return;
        }
        const now = Date.now();
        markersCache.forEach((entry) => {
            const hasData = Array.isArray(entry.data);
            const current = hasData ? entry.data : [];
            const existingIndex = current.findIndex((item) => item.id === marker.id);
            const shouldInclude = !entry.bounds || isCoordinateWithinBounds(marker.coordinate, entry.bounds);
            const markerType = normalizeMarkerType(marker.type);
            const shouldIncludeType = cacheEntryAllowsMarkerType(entry, markerType);

            if ((!shouldInclude || !shouldIncludeType) && existingIndex === -1) {
                return;
            }

            if ((!shouldInclude || !shouldIncludeType) && existingIndex >= 0) {
                entry.data = current.filter((item) => item.id !== marker.id);
                entry.fetchedAt = now;
                entry.error = null;
                return;
            }

            const merged = existingIndex >= 0
                ? current.map((item) => (item.id === marker.id ? marker : item))
                : [marker, ...current];
            entry.data = merged;
            entry.fetchedAt = now;
            entry.error = null;
        });
    }, []);

    const removeMarkerFromCaches = useCallback((markerId) => {
        const normalizedMarkerId = readOptionalString(markerId);
        if (!normalizedMarkerId) {
            return;
        }

        const now = Date.now();
        markersCache.forEach((entry) => {
            if (!Array.isArray(entry?.data)) {
                return;
            }

            const nextData = entry.data.filter((item) => item.id !== normalizedMarkerId);
            if (nextData.length === entry.data.length) {
                return;
            }

            entry.data = nextData;
            entry.fetchedAt = now;
            entry.error = null;
        });
    }, []);

    const registerCreatedMarker = useCallback((created) => {
        if (!shouldKeepDiscoverMarker(created)) {
            removeMarkerFromCaches(created?.id);
            const currentCache = resolveCacheEntry();
            const existing = Array.isArray(currentCache.data) ? currentCache.data : [];

            setState({
                markers: existing.filter((marker) => marker.id !== created?.id),
                isLoading: false,
                error: null,
                lastUpdated: resolveStableLastUpdated(currentCache.fetchedAt || Date.now()),
                markerDataKey: boundsKey,
                isStale: false,
            });

            return created;
        }

        const sanitized = sanitizeMarkers([created], DISCOVER_MARKER_SANITIZE_OPTIONS);
        const sanitizedMarker = sanitized.length > 0 ? sanitized[0] : null;
        if (!sanitizedMarker) {
            throw new Error('Failed to sanitize created map marker');
        }

        updateCachesWithMarker(sanitizedMarker);
        const currentCache = resolveCacheEntry();
        const existing = Array.isArray(currentCache.data) ? currentCache.data : [];
        const hasExisting = existing.some((marker) => marker.id === sanitizedMarker.id);
        const shouldInclude = !resolvedBounds
            || isCoordinateWithinBounds(sanitizedMarker.coordinate, resolvedBounds);
        const shouldIncludeType = cacheEntryAllowsMarkerType(currentCache, normalizeMarkerType(sanitizedMarker.type));
        const merged = shouldInclude && shouldIncludeType
            ? (hasExisting
                ? existing.map((marker) => (
                    marker.id === sanitizedMarker.id ? sanitizedMarker : marker
                ))
                : [sanitizedMarker, ...existing])
            : existing.filter((marker) => marker.id !== sanitizedMarker.id);

        setState({
            markers: merged,
            isLoading: false,
            error: null,
            lastUpdated: resolveStableLastUpdated(currentCache.fetchedAt || Date.now()),
            markerDataKey: boundsKey,
            isStale: false,
        });

        return sanitizedMarker;
    }, [boundsKey, removeMarkerFromCaches, resolvedBounds, resolveCacheEntry, updateCachesWithMarker]);

    const createMarker = useCallback(async (payload) => {
        const created = await createDiscoverMarker(payload);
        return registerCreatedMarker(created);
    }, [registerCreatedMarker]);

    // Domain forms already performed the canonical create. Only project the
    // response into Discover's existing bounded, public-only marker caches.
    const registerCreatedEntity = useCallback((entity) => {
        const type = normalizeMarkerType(entity?.objectType);
        if (type !== 'EVENT' && type !== 'VENUE') {
            throw new Error('Unsupported Discover entity type');
        }
        requireCanonicalUuid(entity?.id, 'objectId');
        return registerCreatedMarker(type === 'VENUE'
            ? toVenueMarker(entity)
            : toScheduledMarker(entity, undefined, type));
    }, [registerCreatedMarker]);

    const updateMarker = useCallback(async (markerId, payload) => {
        const existingMarkerType = findCachedMarkerType(markerId);
        const updated = await updateDiscoverMarker(markerId, payload, existingMarkerType);
        if (!shouldKeepDiscoverMarker(updated)) {
            removeMarkerFromCaches(markerId);
            const currentCache = resolveCacheEntry();
            const existing = Array.isArray(currentCache.data) ? currentCache.data : [];

            setState({
                markers: existing.filter((marker) => marker.id !== markerId),
                isLoading: false,
                error: null,
                lastUpdated: resolveStableLastUpdated(currentCache.fetchedAt || Date.now()),
                markerDataKey: boundsKey,
                isStale: false,
            });

            return updated;
        }

        const sanitized = sanitizeMarkers([updated], DISCOVER_MARKER_SANITIZE_OPTIONS);
        const sanitizedMarker = sanitized.length > 0 ? sanitized[0] : null;
        if (!sanitizedMarker) {
            throw new Error('Failed to sanitize updated map marker');
        }

        updateCachesWithMarker(sanitizedMarker);
        const currentCache = resolveCacheEntry();
        const existing = Array.isArray(currentCache.data) ? currentCache.data : [];
        const hasExisting = existing.some((marker) => marker.id === sanitizedMarker.id);
        const shouldInclude = !resolvedBounds || isCoordinateWithinBounds(sanitizedMarker.coordinate, resolvedBounds);
        const shouldIncludeType = cacheEntryAllowsMarkerType(currentCache, normalizeMarkerType(sanitizedMarker.type));
        const merged = shouldInclude && shouldIncludeType
            ? (hasExisting
                ? existing.map((marker) => (
                    marker.id === sanitizedMarker.id ? sanitizedMarker : marker
                ))
                : [sanitizedMarker, ...existing])
            : existing.filter((marker) => marker.id !== sanitizedMarker.id);

        setState({
            markers: merged,
            isLoading: false,
            error: null,
            lastUpdated: resolveStableLastUpdated(currentCache.fetchedAt || Date.now()),
            markerDataKey: boundsKey,
            isStale: false,
        });

        return sanitizedMarker;
    }, [boundsKey, removeMarkerFromCaches, resolvedBounds, resolveCacheEntry, updateCachesWithMarker]);

    const metadata = useMemo(() => ({
        lastUpdated: state.lastUpdated,
        isStale: state.isStale,
    }), [state.isStale, state.lastUpdated]);

    return {
        markers: state.markers,
        isLoading: state.isLoading,
        error: state.error,
        lastUpdated: state.lastUpdated,
        markerDataKey: state.markerDataKey,
        isStale: state.isStale,
        refresh,
        createMarker,
        registerCreatedEntity,
        updateMarker,
        metadata,
    };
}
